import { Link, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Heart, Menu, X } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/Logo";
import HelpWidget from "@/help/HelpWidget";

const navLinks = [
  { to: "/", label: "Etusivu", exact: true },
  { to: "/pricing", label: "Hinnoittelu" },
  { to: "/ohjeet", label: "Ohjeet" },
  { to: "/ukk", label: "UKK" },
  { to: "/blogi", label: "Blogi" },
  { to: "/company", label: "Ota yhteyttä" },
];

const footerLinks = [
  { to: "/kumppanit", label: "Kumppanuus tilitoimistoille" },
  { to: "/blogi", label: "Blogi" },
  { to: "/privacy", label: "Tietosuojaseloste" },
  { to: "/hyvantekevaisyys", label: "Meidän missio" },
  { to: "/company", label: "Ota yhteyttä" },
];

export default function MarketingLayout() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Scroll to top on route change (unless hash anchor)
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);
  function isActive(to: string, exact?: boolean) {
    return exact ? location.pathname === to : location.pathname.startsWith(to);
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-3">
            <Logo size="md" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 md:flex">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  "relative px-4 py-2 text-sm font-medium transition-colors rounded-lg",
                  isActive(link.to, link.exact)
                    ? "text-primary bg-primary/5"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
              >
                {link.label}
                {isActive(link.to, link.exact) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-0.5 bg-primary rounded-full" />
                )}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/login" className="hidden md:inline-flex">
              <Button variant="ghost" size="sm" className="text-sm font-medium">
                Kirjaudu
              </Button>
            </Link>
            <Link to="/register" className="hidden md:inline-flex">
              <Button size="sm" className="rounded-xl gap-1.5 font-semibold shadow-lg shadow-primary/20">
                Aloita ilmaiseksi <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </Link>

            {/* Mobile menu toggle */}
            <button
              className="md:hidden p-2 text-foreground"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="md:hidden border-t border-border/40 bg-background px-6 py-4 animate-fade-in">
            <nav className="flex flex-col gap-1 mb-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "px-4 py-2.5 text-sm font-medium rounded-xl transition-colors",
                    isActive(link.to, link.exact)
                      ? "text-primary bg-primary/5"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  )}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            <div className="flex flex-col gap-2">
              <Link to="/login" onClick={() => setMobileOpen(false)}>
                <Button variant="outline" className="w-full rounded-xl">Kirjaudu</Button>
              </Link>
              <Link to="/register" onClick={() => setMobileOpen(false)}>
                <Button className="w-full rounded-xl font-semibold">Aloita ilmaiseksi</Button>
              </Link>
            </div>
          </div>
        )}
      </header>

      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="mt-16 border-t border-border/40">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <div className="flex flex-col gap-8 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <Logo size="sm" />
              </div>
              <p className="text-sm text-muted-foreground max-w-xs">
                Matkalasku- ja kilometrikorvauspalvelu yrityksille ja yksityishenkilöille.
              </p>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
              {footerLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* 10% donation banner */}
          <div className="mt-8 rounded-2xl border border-primary/15 bg-primary/[0.03] p-5 flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <Heart className="h-5 w-5 text-primary" fill="currentColor" />
            </div>
            <div>
              <p className="text-sm font-semibold">10 % nuorten harrastuksiin</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Lahjoitamme 10 % jokaisesta maksusta nuorten urheiluun ja harrastetoimintaan.
                Lahjoitus tapahtuu automaattisesti palvelun käytön yhteydessä.
              </p>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-border/40 text-sm text-muted-foreground">
            © {new Date().getFullYear()} Kilometrit.fi
          </div>
        </div>
      </footer>
      <HelpWidget />
    </div>
  );
}
